/**
 * SettingsDirtyContext — the Console's unified save model.
 *
 * The old page mixed autosave-on-toggle with per-section Save buttons, so an
 * operator could never predict whether a change had persisted. In the Console
 * every section holds its edits locally and registers a dirty entry here; one
 * sticky save bar (SettingsSaveBar) aggregates them into "N unsaved changes —
 * Discard / Save changes".
 *
 * Contract for sections:
 * - Track local pending state vs the loaded server value.
 * - Call `useDirtyRegistration(key, isDirty ? { label, save, discard } : null)`.
 *   `save` must return a promise that REJECTS on failure (use mutateAsync) so
 *   Save-all can stop and report; on success the section's own refetch/state
 *   sync clears `isDirty`, which unregisters the entry.
 * - `discard` resets local state to the loaded server value.
 *
 * The provider exposes `saveAll` / `discardAll` for the bar, plus a snapshot
 * of entry labels for the bar's tooltip-style summary. Entries live in a ref
 * (stable identity for unregister-on-unmount) with a version counter to
 * trigger re-renders.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface DirtyEntry {
  /** Human label shown in the save bar summary (e.g. "Slack notifications"). */
  label: string;
  /** Persist this section's pending edits. MUST reject on failure. */
  save: () => Promise<unknown>;
  /** Reset this section's pending edits back to the loaded server values. */
  discard: () => void;
}

interface SettingsDirtyContextValue {
  /** Number of dirty sections. */
  count: number;
  /** Labels of dirty sections, registration order. */
  labels: string[];
  /** True while saveAll is running. */
  isSaving: boolean;
  saveAll: () => Promise<{ saved: number; failed: string[] }>;
  discardAll: () => void;
  register: (key: string, entry: DirtyEntry) => void;
  unregister: (key: string) => void;
}

const SettingsDirtyContext = createContext<SettingsDirtyContextValue | null>(null);

export function SettingsDirtyProvider({ children }: { children: ReactNode }) {
  const entriesRef = useRef<Map<string, DirtyEntry>>(new Map());
  const [version, setVersion] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const register = useCallback((key: string, entry: DirtyEntry) => {
    entriesRef.current.set(key, entry);
    setVersion((v) => v + 1);
  }, []);

  const unregister = useCallback((key: string) => {
    if (entriesRef.current.delete(key)) {
      setVersion((v) => v + 1);
    }
  }, []);

  const saveAll = useCallback(async () => {
    setIsSaving(true);
    const failed: string[] = [];
    let saved = 0;
    try {
      // Sequential on purpose: several sections share the prod_settings row
      // upsert; parallel writes would race on onConflict.
      for (const [key, entry] of Array.from(entriesRef.current.entries())) {
        try {
          await entry.save();
          saved += 1;
        } catch {
          // The section's own onError already toasts the specific message;
          // collect the label so the bar can summarise what remains dirty.
          failed.push(entry.label);
          void key;
        }
      }
    } finally {
      setIsSaving(false);
    }
    return { saved, failed };
  }, []);

  const discardAll = useCallback(() => {
    for (const entry of Array.from(entriesRef.current.values())) {
      entry.discard();
    }
  }, []);

  const value = useMemo<SettingsDirtyContextValue>(() => {
    const labels = Array.from(entriesRef.current.values()).map((e) => e.label);
    return {
      count: entriesRef.current.size,
      labels,
      isSaving,
      saveAll,
      discardAll,
      register,
      unregister,
    };
    // `version` is the invalidation signal for the ref-backed map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, isSaving, saveAll, discardAll, register, unregister]);

  return <SettingsDirtyContext.Provider value={value}>{children}</SettingsDirtyContext.Provider>;
}

export function useSettingsDirty(): SettingsDirtyContextValue {
  const ctx = useContext(SettingsDirtyContext);
  if (!ctx) {
    throw new Error('useSettingsDirty must be used within SettingsDirtyProvider');
  }
  return ctx;
}

/**
 * Section-side helper: register while `entry` is non-null, unregister when it
 * becomes null or the section unmounts. Pass `null` when the section is clean.
 *
 * The entry's callbacks are kept fresh via a ref so sections don't need to
 * memoize `save`/`discard` — only the null/non-null transition and `key`
 * drive effect work.
 */
export function useDirtyRegistration(key: string, entry: DirtyEntry | null) {
  const { register, unregister } = useSettingsDirty();
  const entryRef = useRef<DirtyEntry | null>(entry);
  entryRef.current = entry;
  const isDirty = entry !== null;

  useEffect(() => {
    if (!isDirty) return undefined;
    // Stable wrapper delegates to the latest callbacks.
    register(key, {
      get label() {
        return entryRef.current?.label ?? key;
      },
      save: () => entryRef.current?.save() ?? Promise.resolve(),
      discard: () => entryRef.current?.discard(),
    } as DirtyEntry);
    return () => unregister(key);
  }, [key, isDirty, register, unregister]);
}
